import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  Visibility,
  AttachMoney,
  TrendingUp,
  CreditCard,
  AccountBalance,
  Refresh,
  CheckCircle,
  Cancel,
  Pending,
  Download,
  Email,
  Send
} from '@mui/icons-material';
import axios from 'axios';

interface Payment {
  id: string;
  bookingId?: string | null;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  transactionId?: string;
  createdAt: string;
  updatedAt: string;
  booking?: {
    id: string;
    seatsBooked: number;
    bookingNumber: number;
    passenger: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
      email: string;
      memberId?: string;
    };
    ride: {
      id: string;
      rideNumber: number;
      driver: {
        id: string;
        firstName: string;
        lastName: string;
        preferredName?: string;
        email?: string;
        memberId?: string;
      };
      originCity: {
        name: string;
      };
      destinationCity: {
        name: string;
      };
    };
  } | null;
  creditPurchase?: {
    id: string;
    driver: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
      email: string;
      memberId?: string;
    };
  } | null;
}

interface RefundRequest {
  id: string;
  paymentId: string;
  bookingId: string | null;
  amount: number;
  currency: string;
  reason: string;
  status: string;
  requestedBy: string;
  requestedAt: string;
  createdAt: string;
  payment: {
    stripePaymentId?: string;
    transactionId?: string;
  };
  booking?: {
    id: string;
    seatsBooked: number;
    bookingNumber: number;
    passenger: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
      email: string;
      memberId?: string;
    };
    ride: {
      id: string;
      rideNumber: number;
      driver: {
        id: string;
        firstName: string;
        lastName: string;
        preferredName?: string;
        email: string;
        memberId?: string;
      };
      originCity: {
        name: string;
      };
      destinationCity: {
        name: string;
      };
    };
  };
  requester: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
  };
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const PaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPayments, setTotalPayments] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info', message: string } | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    todayRevenue: 0,
    weekRevenue: 0,
    monthRevenue: 0,
    pendingPayments: 0,
    completedPayments: 0,
    failedPayments: 0
  });

  // Payout states
  const [payouts, setPayouts] = useState<any[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutPage, setPayoutPage] = useState(0);
  const [payoutRowsPerPage, setPayoutRowsPerPage] = useState(25);
  const [totalPayouts, setTotalPayouts] = useState(0);
  const [payoutSearch, setPayoutSearch] = useState('');
  const [payoutFilterStatus, setPayoutFilterStatus] = useState('ALL');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Refund requests states
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<RefundRequest | null>(null);
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundActionNotes, setRefundActionNotes] = useState('');

  // Notification states
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [notifyRecipient, setNotifyRecipient] = useState<{ id: string; name: string; email: string; role: string } | null>(null);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyChannels, setNotifyChannels] = useState<{ email: boolean; in_app: boolean }>({ email: true, in_app: true });
  const [notifySending, setNotifySending] = useState(false);

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    if (tabValue === 0) {
      fetchPayments();
      fetchStats();
    } else if (tabValue === 1) {
      fetchPayouts();
    } else if (tabValue === 2) {
      fetchRefundRequests();
    }
  }, [page, rowsPerPage, search, filterStatus, tabValue, payoutPage, payoutRowsPerPage, payoutSearch, payoutFilterStatus]);

  const fetchPayouts = async () => {
    setPayoutsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (payoutPage + 1).toString(),
        limit: payoutRowsPerPage.toString(),
        ...(payoutSearch && { search: payoutSearch }),
        ...(payoutFilterStatus !== 'ALL' && { status: payoutFilterStatus })
      });

      const response = await axios.get(`${apiUrl}/admin/payments/payouts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPayouts(response.data.payouts || []);
      setTotalPayouts(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
      showAlert('error', 'Failed to load payouts');
    } finally {
      setPayoutsLoading(false);
    }
  };

  const handleReleasePayout = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to manually release this payout early to the driver? This executes the Stripe Connect transfer immediately.')) return;

    setActionInProgress(paymentId);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${apiUrl}/admin/payments/payouts/${paymentId}/release`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', 'Payout manually released and transferred successfully');
        fetchPayouts();
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to release payout');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleHoldPayout = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to place a temporary hold on this driver payout? This will block the background payout scheduler.')) return;

    setActionInProgress(paymentId);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${apiUrl}/admin/payments/payouts/${paymentId}/hold`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', 'Payout placed on hold');
        fetchPayouts();
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to place hold');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleUnholdPayout = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to release the hold on this driver payout? This will restore it to the standard pending queue.')) return;

    setActionInProgress(paymentId);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${apiUrl}/admin/payments/payouts/${paymentId}/unhold`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', 'Payout hold released');
        fetchPayouts();
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to release hold');
    } finally {
      setActionInProgress(null);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(search && { search }),
        ...(filterStatus !== 'ALL' && { status: filterStatus })
      });

      const response = await axios.get(`${apiUrl}/admin/payments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPayments(response.data.payments || []);
      setTotalPayments(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      showAlert('error', 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/payments/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data.stats || stats);
    } catch (error) {
      console.error('Failed to fetch payment stats:', error);
    }
  };

  const fetchRefundRequests = async () => {
    setRefundsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/payments/refunds/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRefundRequests(response.data.refunds || []);
    } catch (error) {
      console.error('Failed to fetch pending refunds:', error);
      showAlert('error', 'Failed to load pending refund requests');
    } finally {
      setRefundsLoading(false);
    }
  };

  const handleProcessRefund = async (refundId: string, approve: boolean) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/payments/refunds/${refundId}/process`,
        { approve, adminNotes: refundActionNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', approve ? 'Refund approved and processed successfully' : 'Refund request rejected');
      setRefundDialog(false);
      setRefundActionNotes('');
      fetchRefundRequests();
      if (tabValue === 0) {
        fetchPayments();
        fetchStats();
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to process refund');
    }
  };

  const handleSendNotification = async () => {
    if (!notifyRecipient || !notifyTitle || !notifyMessage) return;
    setNotifySending(true);
    try {
      const token = localStorage.getItem('adminToken');
      const selectedChannels = Object.keys(notifyChannels).filter(
        c => notifyChannels[c as keyof typeof notifyChannels]
      );
      await axios.post(
        `${apiUrl}/admin/payments/notify`,
        {
          userId: notifyRecipient.id,
          title: notifyTitle,
          message: notifyMessage,
          channels: selectedChannels
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', `Notification sent successfully to ${notifyRecipient.name}`);
      setNotifyDialogOpen(false);
      setNotifyTitle('');
      setNotifyMessage('');
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to send notification');
    } finally {
      setNotifySending(false);
    }
  };

  const triggerNotifyUser = (userId: string, firstName: string, lastName: string, email: string, role: string, defaultSubject: string = '') => {
    setNotifyRecipient({
      id: userId,
      name: `${firstName} ${lastName}`,
      email,
      role
    });
    setNotifyTitle(defaultSubject);
    setNotifyMessage('');
    setNotifyChannels({ email: true, in_app: true });
    setNotifyDialogOpen(true);
  };

  const handleViewPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailsDialog(true);
  };

  const handleRefund = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to process a refund for this payment?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/payments/${paymentId}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Refund processed successfully');
      fetchPayments();
      fetchStats();
      setDetailsDialog(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to process refund');
    }
  };

  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'REFUNDED':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Payments & Payouts Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor transactions, manage driver payouts, and execute escrow early release
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            if (tabValue === 0) {
              fetchPayments();
              fetchStats();
            } else if (tabValue === 1) {
              fetchPayouts();
            } else if (tabValue === 2) {
              fetchRefundRequests();
            }
          }}
        >
          Refresh
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label="Payments & Revenue Overview" />
          <Tab label="Driver Payouts Control" />
          <Tab label="Passenger Refund Requests" />
        </Tabs>
      </Box>

      {/* TAB 0: PAYMENTS & REVENUE */}
      <TabPanel value={tabValue} index={0}>
        {/* Revenue Stats Overview */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <AttachMoney sx={{ fontSize: 48 }} />
            <Typography variant="h4" fontWeight="bold">
              {formatCurrency(stats.totalRevenue)}
            </Typography>
            <Typography variant="body2">Total Revenue</Typography>
          </Paper>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <TrendingUp color="success" sx={{ fontSize: 40 }} />
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(stats.todayRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">Today</Typography>
          </Paper>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <TrendingUp color="info" sx={{ fontSize: 40 }} />
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(stats.weekRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">This Week</Typography>
          </Paper>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <TrendingUp color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(stats.monthRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">This Month</Typography>
          </Paper>
        </Box>

        {/* Payment Status Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.completedPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">Completed</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Pending color="warning" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.pendingPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">Pending</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Cancel color="error" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.failedPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">Failed</Typography>
          </Paper>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by transaction ID, passenger, or driver..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="ALL">All Payments</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="REFUNDED">Refunded</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => {
              setSearch('');
              setFilterStatus('ALL');
            }}>
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Payments Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Passenger</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No payments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                          {payment.transactionId || payment.id.substring(0, 8)}
                        </Typography>
                        {payment.booking && (
                          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                            Ride #{payment.booking.ride.rideNumber} • Booking #{payment.booking.bookingNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.booking ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {payment.booking.passenger.preferredName || payment.booking.passenger.firstName} {payment.booking.passenger.lastName ? payment.booking.passenger.lastName.charAt(0) + '.' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {payment.booking.passenger.email} • {payment.booking.passenger.memberId || 'N/A'}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => triggerNotifyUser(
                                payment.booking!.passenger.id,
                                payment.booking!.passenger.firstName,
                                payment.booking!.passenger.lastName,
                                payment.booking!.passenger.email,
                                'Passenger',
                                `Important notice regarding Transaction ${payment.transactionId || payment.id.substring(0, 8)}`
                              )}
                              title="Send notification/email to Passenger"
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            System Credit Purchase
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.booking ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="body2">
                                {payment.booking.ride.driver.preferredName || payment.booking.ride.driver.firstName} {payment.booking.ride.driver.lastName ? payment.booking.ride.driver.lastName.charAt(0) + '.' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {payment.booking.ride.driver.email} • {payment.booking.ride.driver.memberId || 'N/A'}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => triggerNotifyUser(
                                payment.booking!.ride.driver.id,
                                payment.booking!.ride.driver.firstName,
                                payment.booking!.ride.driver.lastName,
                                payment.booking!.ride.driver.email || '',
                                'Driver',
                                `Important notice regarding Transaction ${payment.transactionId || payment.id.substring(0, 8)}`
                              )}
                              title="Send notification/email to Driver"
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : payment.creditPurchase?.driver ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="body2">
                                {payment.creditPurchase.driver.preferredName || payment.creditPurchase.driver.firstName} {payment.creditPurchase.driver.lastName ? payment.creditPurchase.driver.lastName.charAt(0) + '.' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {payment.creditPurchase.driver.email} • {payment.creditPurchase.driver.memberId || 'N/A'}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => triggerNotifyUser(
                                payment.creditPurchase!.driver.id,
                                payment.creditPurchase!.driver.firstName,
                                payment.creditPurchase!.driver.lastName,
                                payment.creditPurchase!.driver.email,
                                'Driver',
                                `Notice regarding credit package transaction`
                              )}
                              title="Send notification/email to Driver"
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.booking ? (
                          <Typography variant="body2">
                            {payment.booking.ride.originCity.name} → {payment.booking.ride.destinationCity.name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Credit Package Purchase
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(payment.amount, payment.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CreditCard fontSize="small" color="action" />
                          <Typography variant="body2">
                            {payment.paymentMethod || 'Card'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status}
                          size="small"
                          color={getStatusColor(payment.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDateTime(payment.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleViewPayment(payment)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalPayments}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </TabPanel>

      {/* TAB 1: DRIVER PAYOUTS CONTROL */}
      <TabPanel value={tabValue} index={1}>
        {/* Payout Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by driver name, email, or Stripe Connect ID..."
              value={payoutSearch}
              onChange={(e) => setPayoutSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Payout Status</InputLabel>
              <Select
                value={payoutFilterStatus}
                label="Payout Status"
                onChange={(e) => setPayoutFilterStatus(e.target.value)}
              >
                <MenuItem value="ALL">All Payouts</MenuItem>
                <MenuItem value="PENDING">Pending Escrow</MenuItem>
                <MenuItem value="TRANSFERRED">Transferred (Completed)</MenuItem>
                <MenuItem value="HELD">Held (Dispute)</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => {
              setPayoutSearch('');
              setPayoutFilterStatus('ALL');
            }}>
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Payouts Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Driver</TableCell>
                  <TableCell>Passenger</TableCell>
                  <TableCell>Fare Total</TableCell>
                  <TableCell>Stripe Fee</TableCell>
                  <TableCell>Net Payout</TableCell>
                  <TableCell>Escrow Payout Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : payouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No payouts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payouts.map((payout) => {
                    const booking = payout.booking;
                    const driver = booking?.ride?.driver;
                    const passenger = booking?.passenger;

                    // Calculate payout details
                    const totalAmount = booking?.totalAmount || payout.amount;
                    const stripeFee = (totalAmount * 0.029) + 0.30;
                    const netPayout = Math.max(0, totalAmount - stripeFee);

                    return (
                      <TableRow key={payout.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {driver ? `${driver.preferredName || driver.firstName} ${driver.lastName || ''}` : 'Unknown Driver'}
                              </Typography>
                              {driver && (
                                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                  {driver.stripeConnectAccountId || 'No Stripe ID'} • {driver.memberId || 'No Member ID'}
                                </Typography>
                              )}
                            </Box>
                            {driver && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => triggerNotifyUser(
                                  driver.id,
                                  driver.firstName,
                                  driver.lastName || '',
                                  driver.email || '',
                                  'Driver',
                                  `Notice regarding your payout for booking ${booking?.id.substring(0, 8) || ''}`
                                )}
                                title="Send notification/email to Driver"
                              >
                                <Email fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {passenger ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2">
                                {`${passenger.preferredName || passenger.firstName} ${passenger.lastName || ''}`}
                              </Typography>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => triggerNotifyUser(
                                  passenger.id,
                                  passenger.firstName,
                                  passenger.lastName || '',
                                  passenger.email || '',
                                  'Passenger',
                                  `Notice regarding payment for booking ${booking?.id.substring(0, 8) || ''}`
                                )}
                                title="Send notification/email to Passenger"
                              >
                                <Email fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatCurrency(totalAmount)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatCurrency(stripeFee)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {formatCurrency(netPayout)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payout.transferEligibleAt ? (
                            <Typography variant="body2" color={new Date(payout.transferEligibleAt) <= new Date() ? 'success.main' : 'text.primary'}>
                              {formatDateTime(payout.transferEligibleAt)}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payout.transferStatus}
                            size="small"
                            color={
                              payout.transferStatus === 'TRANSFERRED'
                                ? 'success'
                                : payout.transferStatus === 'HELD'
                                ? 'error'
                                : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {payout.transferStatus === 'PENDING' && (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  onClick={() => handleReleasePayout(payout.id)}
                                  disabled={actionInProgress === payout.id}
                                >
                                  Release
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={() => handleHoldPayout(payout.id)}
                                  disabled={actionInProgress === payout.id}
                                >
                                  Hold
                                </Button>
                              </>
                            )}
                            {payout.transferStatus === 'HELD' && (
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                onClick={() => handleUnholdPayout(payout.id)}
                                disabled={actionInProgress === payout.id}
                              >
                                Release Hold
                              </Button>
                            )}
                            {payout.transferStatus === 'TRANSFERRED' && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Transferred
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalPayouts}
            page={payoutPage}
            onPageChange={(e, newPage) => setPayoutPage(newPage)}
            rowsPerPage={payoutRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPayoutRowsPerPage(parseInt(e.target.value, 10));
              setPayoutPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </TabPanel>

      {/* TAB 2: PASSENGER REFUND REQUESTS */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Pending Refund Requests & Complaints
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Passengers request refunds for credit card payments due to ride cancellations or support disputes.
            Review reasons and process refunds securely.
          </Typography>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Passenger / Requester</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Reason for Request (Complaint)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : refundRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No pending refund requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  refundRequests.map((refund) => {
                    const passenger = refund.booking?.passenger || refund.requester;
                    const driver = refund.booking?.ride?.driver;
                    return (
                      <TableRow key={refund.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {passenger.preferredName || passenger.firstName} {passenger.lastName || ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {passenger.email}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => triggerNotifyUser(
                                passenger.id,
                                passenger.firstName,
                                passenger.lastName || '',
                                passenger.email,
                                'Passenger',
                                `Notice regarding your refund request for booking ${refund.bookingId?.substring(0, 8) || ''}`
                              )}
                              title="Notify Passenger"
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {driver ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box>
                                <Typography variant="body2">
                                  {driver.preferredName || driver.firstName} {driver.lastName || ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {driver.email}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => triggerNotifyUser(
                                  driver.id,
                                  driver.firstName,
                                  driver.lastName || '',
                                  driver.email,
                                  'Driver',
                                  `Notice regarding passenger refund request for booking ${refund.bookingId?.substring(0, 8) || ''}`
                                )}
                                title="Notify Driver"
                              >
                                <Email fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(refund.amount, refund.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>
                          <Typography variant="body2" noWrap title={refund.reason}>
                            {refund.reason}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={refund.status}
                            size="small"
                            color="warning"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDateTime(refund.requestedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => {
                              setSelectedRefund(refund);
                              setRefundActionNotes('');
                              setRefundDialog(true);
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Payment Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Payment Details
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={3}>
                {/* Transaction Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Transaction Information
                  </Typography>
                  <Paper sx={{ p: 2 }} elevation={2}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Transaction ID:</Typography>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                          {selectedPayment.transactionId || selectedPayment.id}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Amount:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                        <Typography variant="body2">{selectedPayment.paymentMethod || 'Card'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip
                          label={selectedPayment.status}
                          size="small"
                          color={getStatusColor(selectedPayment.status)}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Created:</Typography>
                        <Typography variant="body2">{formatDateTime(selectedPayment.createdAt)}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Box>

                <Divider />

                {/* Booking Info */}
                {selectedPayment.booking ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Booking Information
                    </Typography>
                    <Paper sx={{ p: 2 }} elevation={2}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px dashed #ccc', pb: 1 }}>
                          <Typography variant="body2" color="primary" fontWeight="bold">Ride #{selectedPayment.booking.ride.rideNumber}</Typography>
                          <Typography variant="body2" color="primary" fontWeight="bold">Booking #{selectedPayment.booking.bookingNumber}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Passenger:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedPayment.booking.passenger.preferredName || selectedPayment.booking.passenger.firstName} {selectedPayment.booking.passenger.lastName ? selectedPayment.booking.passenger.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {selectedPayment.booking.passenger.email} • {selectedPayment.booking.passenger.memberId || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Driver:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedPayment.booking.ride.driver.preferredName || selectedPayment.booking.ride.driver.firstName} {selectedPayment.booking.ride.driver.lastName ? selectedPayment.booking.ride.driver.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {selectedPayment.booking.ride.driver.email} • {selectedPayment.booking.ride.driver.memberId || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Route:</Typography>
                          <Typography variant="body2">
                            {selectedPayment.booking.ride.originCity.name} → {selectedPayment.booking.ride.destinationCity.name}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Seats Booked:</Typography>
                          <Typography variant="body2">{selectedPayment.booking.seatsBooked}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>
                ) : selectedPayment.creditPurchase ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Driver Credit Purchase Information
                    </Typography>
                    <Paper sx={{ p: 2 }} elevation={2}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Driver:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedPayment.creditPurchase.driver.preferredName || selectedPayment.creditPurchase.driver.firstName} {selectedPayment.creditPurchase.driver.lastName || ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedPayment.creditPurchase.driver.email}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Purchase Item:</Typography>
                          <Typography variant="body2">Driver Ride Posting Credits</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>
                ) : null}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPayment && selectedPayment.status === 'COMPLETED' && (
            <Button
              onClick={() => handleRefund(selectedPayment.id)}
              color="error"
              variant="outlined"
            >
              Process Refund
            </Button>
          )}
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Review Refund Request Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Refund Request</DialogTitle>
        <DialogContent>
          {selectedRefund && (
            <Box sx={{ pt: 1 }}>
              <Stack spacing={2}>
                <Typography variant="body2">
                  <strong>Requester:</strong> {selectedRefund.booking?.passenger.firstName || selectedRefund.requester.firstName} {selectedRefund.booking?.passenger.lastName || selectedRefund.requester.lastName} ({selectedRefund.booking?.passenger.email || selectedRefund.requester.email})
                </Typography>
                {selectedRefund.booking?.ride.driver && (
                  <Typography variant="body2">
                    <strong>Driver:</strong> {selectedRefund.booking.ride.driver.firstName} {selectedRefund.booking.ride.driver.lastName}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Refund Amount:</strong> {formatCurrency(selectedRefund.amount, selectedRefund.currency)}
                </Typography>
                <Typography variant="body2">
                  <strong>Reason/Complaint:</strong>
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedRefund.reason}
                  </Typography>
                </Paper>

                <TextField
                  label="Admin Notes"
                  placeholder="Enter notes about this decision (sent to driver/passenger or logged internally)..."
                  multiline
                  rows={3}
                  fullWidth
                  value={refundActionNotes}
                  onChange={(e) => setRefundActionNotes(e.target.value)}
                />
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handleProcessRefund(selectedRefund!.id, false)}
            color="error"
            variant="outlined"
          >
            Reject Refund
          </Button>
          <Button
            onClick={() => handleProcessRefund(selectedRefund!.id, true)}
            color="success"
            variant="contained"
          >
            Approve & Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={notifyDialogOpen} onClose={() => setNotifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notification / Email</DialogTitle>
        <DialogContent>
          {notifyRecipient && (
            <Box sx={{ pt: 1 }}>
              <Stack spacing={2.5}>
                <Typography variant="body2">
                  <strong>Recipient:</strong> {notifyRecipient.name} ({notifyRecipient.email})
                  <Chip label={notifyRecipient.role} size="small" sx={{ ml: 1 }} color={notifyRecipient.role === 'Driver' ? 'primary' : 'secondary'} />
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifyChannels.in_app}
                        onChange={(e) => setNotifyChannels({ ...notifyChannels, in_app: e.target.checked })}
                      />
                    }
                    label="In-App Notification"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifyChannels.email}
                        onChange={(e) => setNotifyChannels({ ...notifyChannels, email: e.target.checked })}
                      />
                    }
                    label="Email (via Resend/Gmail)"
                  />
                </Box>

                <TextField
                  label="Subject / Notification Title"
                  fullWidth
                  required
                  value={notifyTitle}
                  onChange={(e) => setNotifyTitle(e.target.value)}
                  placeholder="e.g., Important update regarding your dispute"
                />

                <TextField
                  label="Message Content"
                  multiline
                  rows={5}
                  fullWidth
                  required
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  placeholder="Write details of the issue, status updates, or requested actions here..."
                />
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            color="primary"
            variant="contained"
            disabled={notifySending || !notifyTitle || !notifyMessage || (!notifyChannels.email && !notifyChannels.in_app)}
            startIcon={notifySending ? <CircularProgress size={20} /> : <Send />}
          >
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentsPage;
